import { buildPocoRankingColumns, getPocoRankingRoundCount } from "../utils/pocoRankingPresentation";
import { useMemo } from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import ResponsiveRankingTable, {
  ResponsiveRankingColumn,
  ResponsiveRankingRow,
} from "../../../shared/components/ResponsiveRankingTable";
import { ChallengeDetail, ChallengeMatch } from "../types/challengeAdmin";
import { normalizePairId } from "../utils/challengePairs";
import { resolvePocoPoints } from "../utils/pocoSchedule";
import "./ChallengeEngine.css";
import "./ChallengeResultsView.css";

type ChallengeResultsViewProps = {
  challenge: ChallengeDetail;
  compact?: boolean;
};

type RankingRow = {
  id: string;
  label: string;
  points: number;
  roundPoints: number[];
  isPocoChampion?: boolean;
  isPocoFinalLoser?: boolean;
  gamesWon: number;
  gamesLost: number;
  gamesBalance: number;
  wins: number;
  losses: number;
  matches: number;
};

type RankingAvatar = {
  avatarUrl: string;
  name: string;
  sex: "male" | "female" | "other" | "";
};

function normalizeCpf(value?: string | null) {
  return String(value || "").replace(/\D/g, "");
}

function normalizeAvatarSex(
  value?: string | null,
): RankingAvatar["sex"] {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "male" || normalized === "masculino") return "male";
  if (normalized === "female" || normalized === "feminino") return "female";
  if (normalized === "other" || normalized === "outro") return "other";
  return "";
}

function resolveSuper8RoundGroup(
  roundIndex: number,
  totalRounds: number,
  isSuper8Challenge: boolean,
  participantsCount: number,
  status: ChallengeDetail["status"]
): "A" | "B" | null {
  if (!isSuper8Challenge) return null;
  if (status === "pending") return null;
  if (Number.isFinite(participantsCount) && participantsCount > 0 && participantsCount <= 8) {
    return null;
  }
  const isCombined = totalRounds === 14;
  if (!isCombined) return null;
  const splitPoint = 7;
  return roundIndex < splitPoint ? "A" : "B";
}

function parseScore(score: number | "" | undefined) {
  const value = Number(score);
  return Number.isFinite(value) ? value : null;
}

function resolvePocoWinnerSide(match: ChallengeMatch) {
  const score1 = parseScore(match?.score1);
  const score2 = parseScore(match?.score2);
  if (score1 === null || score2 === null || score1 === score2) return null;
  return score1 > score2 ? "dupla1" : "dupla2";
}

function isPendingPocoMatch(match: ChallengeMatch) {
  const dupla1Id = String(match?.dupla1Id || "").trim();
  const dupla2Id = String(match?.dupla2Id || "").trim();
  if (!dupla1Id || !dupla2Id) return true;
  const pendingLabel = String(siteTexts.challengeEngine.labels.removedPair || "")
    .trim()
    .toLowerCase();
  const label1 = String((match as any)?.dupla1Label || "")
    .trim()
    .toLowerCase();
  const label2 = String((match as any)?.dupla2Label || "")
    .trim()
    .toLowerCase();
  const isPendingLabel = (value: string) =>
    value === pendingLabel || value === "dupla removida";
  return isPendingLabel(label1) && isPendingLabel(label2);
}

function isPendingPairLabel(value: unknown) {
  const pendingLabel = String(siteTexts.challengeEngine.labels.removedPair || "")
    .trim()
    .toLowerCase();
  const label = String(value || "").trim().toLowerCase();
  return !label || label === pendingLabel || label === "dupla removida";
}

function calculateRanking(
  pairs: ChallengeDetail["pairs"],
  matches: Array<ChallengeMatch & { dupla1Label: string; dupla2Label: string }>
) {
  const headToHead = (pairA: string, pairB: string) => {
    const match = matches.find(
      (item) => {
        const dupla1Id = normalizePairId(item.dupla1Id);
        const dupla2Id = normalizePairId(item.dupla2Id);
        return (
          (dupla1Id === pairA && dupla2Id === pairB) ||
          (dupla1Id === pairB && dupla2Id === pairA)
        );
      }
    );
    if (!match) return 0;

    const score1 = parseScore(match.score1);
    const score2 = parseScore(match.score2);
    if (score1 === null || score2 === null) return 0;

    const pairAScore = normalizePairId(match.dupla1Id) === pairA ? score1 : score2;
    const pairBScore = normalizePairId(match.dupla1Id) === pairA ? score2 : score1;
    if (pairAScore === pairBScore) return 0;
    return pairAScore > pairBScore ? 1 : -1;
  };

  return pairs
    .map<RankingRow>((pair) => {
      const pairId = normalizePairId(pair.id);
      let gamesWon = 0;
      let gamesLost = 0;
      let wins = 0;
      let losses = 0;

      matches.forEach((match) => {
        const score1 = parseScore(match.score1);
        const score2 = parseScore(match.score2);
        if (score1 === null || score2 === null) return;

        if (normalizePairId(match.dupla1Id) === pairId) {
          gamesWon += score1;
          gamesLost += score2;
          if (score1 > score2) wins += 1;
          if (score1 < score2) losses += 1;
        }
        if (normalizePairId(match.dupla2Id) === pairId) {
          gamesWon += score2;
          gamesLost += score1;
          if (score2 > score1) wins += 1;
          if (score2 < score1) losses += 1;
        }
      });

      return {
        id: pairId || pair.id,
        label: pair.label,
        points: 0,
        roundPoints: [],
        gamesWon,
        gamesLost,
        gamesBalance: gamesWon - gamesLost,
        wins,
        losses,
        matches: wins + losses,
      };
    })
    .sort((a, b) => {
      if (b.wins !== a.wins) return b.wins - a.wins;
      if (b.gamesBalance !== a.gamesBalance) return b.gamesBalance - a.gamesBalance;
      if (b.gamesWon !== a.gamesWon) return b.gamesWon - a.gamesWon;
      if (a.gamesLost !== b.gamesLost) return a.gamesLost - b.gamesLost;
      const h2h = headToHead(a.id, b.id);
      if (h2h !== 0) return -h2h;
      const labelDiff = a.label.localeCompare(b.label, "pt-BR");
      if (labelDiff !== 0) return labelDiff;
      return String(a.id || "").localeCompare(String(b.id || ""), "pt-BR");
    });
}

function calculatePocoRanking(
  pairs: ChallengeDetail["pairs"],
  rounds: ChallengeDetail["roundsDetailed"]
) {
  const totalRounds = getPocoRankingRoundCount(rounds);
  const finalRoundIndex = totalRounds > 0 ? totalRounds - 1 : -1;
  const lastScoringRoundIndex = finalRoundIndex > 1 ? finalRoundIndex - 1 : totalRounds - 1;
  let finalWinnerId = "";
  let finalLoserId = "";
  const finalMatch =
    finalRoundIndex >= 0 && Array.isArray(rounds?.[finalRoundIndex])
      ? rounds[finalRoundIndex][0]
      : null;
  if (finalMatch) {
    const score1 = parseScore(finalMatch.score1);
    const score2 = parseScore(finalMatch.score2);
    const dupla1Id = normalizePairId(finalMatch.dupla1Id);
    const dupla2Id = normalizePairId(finalMatch.dupla2Id);
    if (
      score1 !== null &&
      score2 !== null &&
      score1 !== score2 &&
      dupla1Id &&
      dupla2Id
    ) {
      finalWinnerId = score1 > score2 ? dupla1Id : dupla2Id;
      finalLoserId = score1 > score2 ? dupla2Id : dupla1Id;
    }
  }

  const rowsById = new Map<string, RankingRow>();
  (pairs || []).forEach((pair) => {
    const pairId = normalizePairId(pair.id);
    if (!pairId) return;
    rowsById.set(pairId, {
      id: pairId || pair.id,
      label: pair.label,
      points: 0,
      roundPoints: [],
      gamesWon: 0,
      gamesLost: 0,
      gamesBalance: 0,
      wins: 0,
      losses: 0,
      matches: 0,
    });
  });

  (rounds || []).forEach((round, roundIndex) => {
    (round || []).forEach((match, courtIndex) => {
      const score1 = parseScore(match.score1);
      const score2 = parseScore(match.score2);
      if (score1 === null || score2 === null || score1 === score2) return;

      const row1 = rowsById.get(normalizePairId(match.dupla1Id));
      const row2 = rowsById.get(normalizePairId(match.dupla2Id));
      if (!row1 || !row2) return;

      row1.gamesWon += score1;
      row1.gamesLost += score2;
      row2.gamesWon += score2;
      row2.gamesLost += score1;
      row1.matches += 1;
      row2.matches += 1;

      const pair1Won = score1 > score2;
      if (pair1Won) {
        row1.wins += 1;
        row2.losses += 1;
      } else {
        row2.wins += 1;
        row1.losses += 1;
      }

      if (roundIndex > 0 && roundIndex < finalRoundIndex) {
        const pair1RoundPoints = resolvePocoPoints(
          courtIndex,
          pair1Won,
          round.length,
        );
        const pair2RoundPoints = resolvePocoPoints(
          courtIndex,
          !pair1Won,
          round.length,
        );
        row1.points += pair1RoundPoints;
        row2.points += pair2RoundPoints;
        row1.roundPoints[roundIndex] =
          Number(row1.roundPoints[roundIndex] || 0) + pair1RoundPoints;
        row2.roundPoints[roundIndex] =
          Number(row2.roundPoints[roundIndex] || 0) + pair2RoundPoints;
      }
    });
  });

  const compareByPoints = (a: RankingRow, b: RankingRow) => {
    if (b.points !== a.points) return b.points - a.points;
    const maxRoundIndex = Math.max(
      Math.min(a.roundPoints.length - 1, lastScoringRoundIndex),
      Math.min(b.roundPoints.length - 1, lastScoringRoundIndex),
    );
    for (let roundIndex = maxRoundIndex; roundIndex >= 1; roundIndex -= 1) {
      const diff =
        Number(b.roundPoints[roundIndex] || 0) -
        Number(a.roundPoints[roundIndex] || 0);
      if (diff !== 0) return diff;
    }
    return String(a.label || "").localeCompare(String(b.label || ""));
  };

  const baseRanking = Array.from(rowsById.values())
    .map((row) => ({
      ...row,
      isPocoChampion: finalWinnerId ? row.id === finalWinnerId : false,
      isPocoFinalLoser: finalLoserId ? row.id === finalLoserId : false,
      gamesBalance: row.gamesWon - row.gamesLost,
    }))
    .sort(compareByPoints);

  if (!finalWinnerId || !finalLoserId) {
    return baseRanking;
  }

  return [...baseRanking].sort((a, b) => {
    const priorityA =
      a.id === finalWinnerId ? 0 : a.id === finalLoserId ? 1 : 2;
    const priorityB =
      b.id === finalWinnerId ? 0 : b.id === finalLoserId ? 1 : 2;
    if (priorityA !== priorityB) return priorityA - priorityB;
    return compareByPoints(a, b);
  });
}

function calculateSuper8Ranking(
  participants: ChallengeDetail["participants"],
  pairs: ChallengeDetail["pairs"],
  matches: Array<ChallengeMatch & { dupla1Label: string; dupla2Label: string }>
) {
  const nameByCpf = (participants || []).reduce<Record<string, string>>((acc, item) => {
    const cpf = String(item?.cpf || "").trim();
    if (!cpf) return acc;
    acc[cpf] = String(item?.name || "").trim() || siteTexts.challengeEngine.labels.player;
    return acc;
  }, {});

  const pairById = (pairs || []).reduce<Record<string, ChallengeDetail["pairs"][number]>>(
    (acc, pair) => {
      const pairId = normalizePairId(pair.id);
      if (pairId) acc[pairId] = pair;
      return acc;
    },
    {}
  );

  const rows = new Map<string, RankingRow>();
  const ensureRow = (cpf: string) => {
    const safeCpf = String(cpf || "").trim();
    if (!safeCpf) return null;
    const existing = rows.get(safeCpf);
    if (existing) return existing;
    const created: RankingRow = {
      id: safeCpf,
      label: nameByCpf[safeCpf] || siteTexts.challengeEngine.labels.player,
      points: 0,
      roundPoints: [],
      gamesWon: 0,
      gamesLost: 0,
      gamesBalance: 0,
      wins: 0,
      losses: 0,
      matches: 0,
    };
    rows.set(safeCpf, created);
    return created;
  };

  (participants || []).forEach((item) => {
    const cpf = String(item?.cpf || "").trim();
    if (!cpf) return;
    ensureRow(cpf);
  });

  matches.forEach((match) => {
    const score1 = parseScore(match.score1);
    const score2 = parseScore(match.score2);
    if (score1 === null || score2 === null) return;
    if (score1 === score2) return;

    const pair1 = pairById[normalizePairId(match.dupla1Id)];
    const pair2 = pairById[normalizePairId(match.dupla2Id)];
    if (!pair1 || !pair2) return;

    const pair1Players = [pair1.cpf1, pair1.cpf2].map((cpf) => String(cpf || "").trim()).filter(Boolean);
    const pair2Players = [pair2.cpf1, pair2.cpf2].map((cpf) => String(cpf || "").trim()).filter(Boolean);

    pair1Players.forEach((cpf) => {
      const row = ensureRow(cpf);
      if (!row) return;
      row.matches += 1;
      row.gamesWon += score1;
      row.gamesLost += score2;
      if (score1 > score2) row.wins += 1;
      else row.losses += 1;
    });

    pair2Players.forEach((cpf) => {
      const row = ensureRow(cpf);
      if (!row) return;
      row.matches += 1;
      row.gamesWon += score2;
      row.gamesLost += score1;
      if (score2 > score1) row.wins += 1;
      else row.losses += 1;
    });
  });

  const list = Array.from(rows.values()).map((row) => ({
    ...row,
    gamesBalance: row.gamesWon - row.gamesLost,
  }));

  // Super 8: 1) vitórias 2) games ganhos 3) games perdidos
  return list.sort((a, b) => {
    if (b.wins !== a.wins) return b.wins - a.wins;
    if (b.gamesWon !== a.gamesWon) return b.gamesWon - a.gamesWon;
    if (a.gamesLost !== b.gamesLost) return a.gamesLost - b.gamesLost;
    return String(a.label || "").localeCompare(String(b.label || ""));
  });
}

export default function ChallengeResultsView({
  challenge,
  compact = false,
}: ChallengeResultsViewProps) {
  const flattenedMatches = useMemo(
    () => (challenge.roundsDetailed || []).flat(),
    [challenge]
  );
  const isSuper8 = challenge.challengeType === "super8";
  const isPoco = challenge.challengeType === "poco";
  const pairLabelById = useMemo(
    () =>
      (challenge.pairs || []).reduce<Record<string, string>>((acc, pair) => {
        const pairId = normalizePairId(pair.id);
        if (pairId) acc[pairId] = String(pair.label || "").trim();
        return acc;
      }, {}),
    [challenge.pairs],
  );
  const participantAvatarByCpf = useMemo(() => {
    const avatars = new Map<string, RankingAvatar>();
    const fallbackSex = normalizeAvatarSex(challenge.categoryGender);

    (challenge.participants || []).forEach((participant) => {
      const cpf = normalizeCpf(participant.cpf);
      if (!cpf) return;
      avatars.set(cpf, {
        avatarUrl: String(participant.avatarUrl || "").trim(),
        name: String(participant.name || "").trim() || "Jogador",
        sex: normalizeAvatarSex(participant.sex) || fallbackSex,
      });
    });

    (challenge.registrations || []).forEach((registration) => {
      const player1Cpf = normalizeCpf(registration.player1Cpf);
      if (player1Cpf) {
        avatars.set(player1Cpf, {
          avatarUrl: String(registration.player1AvatarUrl || "").trim(),
          name: String(registration.player1Name || "").trim() || "Jogador",
          sex: normalizeAvatarSex(registration.player1Sex) || fallbackSex,
        });
      }
      const player2Cpf = normalizeCpf(registration.player2Cpf);
      if (player2Cpf) {
        avatars.set(player2Cpf, {
          avatarUrl: String(registration.player2AvatarUrl || "").trim(),
          name: String(registration.player2Name || "").trim() || "Jogador",
          sex: normalizeAvatarSex(registration.player2Sex) || fallbackSex,
        });
      }
    });

    return avatars;
  }, [challenge]);
  const pairAvatarsById = useMemo(
    () =>
      (challenge.pairs || []).reduce<Record<string, RankingAvatar[]>>(
        (acc, pair) => {
          const pairId = normalizePairId(pair.id);
          if (!pairId) return acc;
          const fallbackSex = normalizeAvatarSex(challenge.categoryGender);
          acc[pairId] = [
            {
              cpf: pair.cpf1,
              name: pair.nome1,
              avatarUrl: pair.player1AvatarUrl,
              sex: pair.player1Sex,
            },
            {
              cpf: pair.cpf2,
              name: pair.nome2,
              avatarUrl: pair.player2AvatarUrl,
              sex: pair.player2Sex,
            },
          ]
            .filter((member) => String(member.name || member.cpf || "").trim())
            .map((member) => {
              const participant = participantAvatarByCpf.get(
                normalizeCpf(member.cpf),
              );
              return {
                avatarUrl:
                  String(member.avatarUrl || "").trim() ||
                  participant?.avatarUrl ||
                  "",
                name:
                  String(member.name || "").trim() ||
                  participant?.name ||
                  "Jogador",
                sex:
                  normalizeAvatarSex(member.sex) ||
                  participant?.sex ||
                  fallbackSex,
              };
            });
          return acc;
        },
        {},
      ),
    [challenge, participantAvatarByCpf],
  );
  const ranking = useMemo(
    () =>
      isSuper8
        ? calculateSuper8Ranking(challenge.participants || [], challenge.pairs || [], flattenedMatches)
        : isPoco
          ? calculatePocoRanking(challenge.pairs || [], challenge.roundsDetailed || [])
        : calculateRanking(challenge.pairs || [], flattenedMatches),
    [challenge, flattenedMatches, isPoco, isSuper8]
  );
  const pocoPointsByPairId = useMemo(() => {
    if (!isPoco) return {} as Record<string, number>;
    return ranking.reduce<Record<string, number>>((acc, row) => {
      acc[row.id] = row.points;
      return acc;
    }, {});
  }, [isPoco, ranking]);
  const maxMatches = useMemo(
    () => (ranking.length ? Math.max(...ranking.map((item) => item.matches)) : 0),
    [ranking]
  );
  const pocoRoundCount = useMemo(
    () => (isPoco ? getPocoRankingRoundCount(challenge.roundsDetailed) : 0),
    [challenge.roundsDetailed, isPoco]
  );
  const columns = useMemo<Array<ResponsiveRankingColumn<RankingRow>>>(
    () => {
      const winsColumn: ResponsiveRankingColumn<RankingRow> = {
        id: "wins",
        header: siteTexts.challengeEngine.labels.wins,
        mobileLabel: siteTexts.challengeEngine.labels.wins,
        render: (row) => row.wins,
      };
      const base: Array<ResponsiveRankingColumn<RankingRow>> = [
      {
        id: "balance",
        header: siteTexts.challengeEngine.labels.balance,
        mobileLabel: siteTexts.challengeEngine.labels.balance,
        render: (row) => row.gamesBalance,
      },
      {
        id: "matches",
        header: siteTexts.challengeEngine.labels.matches,
        mobileLabel: siteTexts.challengeEngine.labels.matches,
        render: (row) => row.matches,
      },
      {
        id: "gamesWon",
        header: siteTexts.challengeEngine.labels.gamesWon,
        mobileLabel: siteTexts.challengeEngine.labels.gamesWon,
        render: (row) => row.gamesWon,
      },
      {
        id: "gamesLost",
        header: siteTexts.challengeEngine.labels.gamesLost,
        mobileLabel: siteTexts.challengeEngine.labels.gamesLost,
        render: (row) => row.gamesLost,
      },
      winsColumn,
      {
        id: "losses",
        header: siteTexts.challengeEngine.labels.losses,
        mobileLabel: siteTexts.challengeEngine.labels.losses,
        render: (row) => row.losses,
      },
      ];
      if (isSuper8) {
        return [winsColumn, ...base.filter((column) => column.id !== "wins")];
      }
      if (!isPoco) return base;
      return buildPocoRankingColumns<RankingRow>(pocoRoundCount);
    },
    [isPoco, isSuper8, pocoRoundCount]
  );
  const rows = useMemo<Array<ResponsiveRankingRow<RankingRow>>>(
    () =>
      ranking.map((item, index) => {
        const stateClasses = [
          index === 0 ? "is-leader" : "",
          !isPoco && item.matches < maxMatches ? "has-less-matches" : "",
        ]
          .filter(Boolean)
          .join(" ");
        return {
          id: item.id,
          rank: index + 1,
          title: item.label,
          avatars: isSuper8
            ? [participantAvatarByCpf.get(normalizeCpf(item.id))].filter(
                (avatar): avatar is RankingAvatar => Boolean(avatar),
              )
            : pairAvatarsById[normalizePairId(item.id)] || [],
          data: item,
          rowClassName: stateClasses,
          mobileItemClassName: stateClasses,
        };
      }),
    [
      isPoco,
      isSuper8,
      maxMatches,
      pairAvatarsById,
      participantAvatarByCpf,
      ranking,
    ]
  );
  const titleHeader = isSuper8
    ? siteTexts.challengeEngine.labels.player
    : siteTexts.challengeEngine.labels.pair;

  return (
    <div className={`challenge-results-view ${compact ? "compact" : ""}`.trim()}>
      <h2 className="ranking-title topo">{siteTexts.challengeEngine.labels.ranking}</h2>
      {isPoco && (
        <p className="vazio">
          Critérios: Final define campeã → Total → R
          {Math.max(2, pocoRoundCount - 1)}…R2.
        </p>
      )}
      <ResponsiveRankingTable
        ariaLabel={siteTexts.challengeEngine.labels.ranking}
        rankHeader={siteTexts.challengeEngine.labels.pos}
        titleHeader={titleHeader}
        columns={columns}
        rows={rows}
      />

      <h2 className="ranking-title">Resultados dos games</h2>
      {(challenge.roundsDetailed || []).length === 0 && (
        <p className="vazio">Nenhuma rodada encontrada.</p>
      )}
      {(challenge.roundsDetailed || []).map((round, roundIndex) => {
        const roundGroup = resolveSuper8RoundGroup(
          roundIndex,
          (challenge.roundsDetailed || []).length,
          isSuper8,
          Number(challenge.participantsCount || challenge.participantCpfs?.length || 0),
          challenge.status
        );
        const roundClassName = ["rodada", roundGroup ? `rodada-grupo-${roundGroup.toLowerCase()}` : ""]
          .filter(Boolean)
          .join(" ");
        return (
          <div key={`round-${roundIndex}`} className={roundClassName}>
            <h3 className="rodada-titulo">
              {isPoco && roundIndex === (challenge.roundsDetailed || []).length - 1
                ? "Final"
                : `${siteTexts.challengeEngine.labels.round} ${roundIndex + 1}`}
              {roundGroup && (
                <span className={`rodada-grupo-badge group-${roundGroup.toLowerCase()}`}>
                  Grupo {roundGroup}
                </span>
              )}
            </h3>
            {isPoco &&
              round.length > 0 &&
              round.every((match) => {
                const pair1BaseLabel = isPendingPairLabel(match.dupla1Label)
                  ? pairLabelById[normalizePairId(match.dupla1Id)] ||
                    match.dupla1Label
                  : match.dupla1Label;
                const pair2BaseLabel = isPendingPairLabel(match.dupla2Label)
                  ? pairLabelById[normalizePairId(match.dupla2Id)] ||
                    match.dupla2Label
                  : match.dupla2Label;
                return (
                  isPendingPocoMatch(match) &&
                  isPendingPairLabel(pair1BaseLabel) &&
                  isPendingPairLabel(pair2BaseLabel)
                );
              }) && (
                <div className="jogo jogo-pendente">
                  <span className="dupla">
                    {siteTexts.challengeEngine.labels.removedPair}
                  </span>
                </div>
              )}
            {round.map((match, matchIndex) => {
              const score1 = parseScore(match.score1);
              const score2 = parseScore(match.score2);
              const pair1BaseLabel = isPendingPairLabel(match.dupla1Label)
                ? pairLabelById[normalizePairId(match.dupla1Id)] ||
                  match.dupla1Label
                : match.dupla1Label;
              const pair2BaseLabel = isPendingPairLabel(match.dupla2Label)
                ? pairLabelById[normalizePairId(match.dupla2Id)] ||
                  match.dupla2Label
                : match.dupla2Label;
              const isPendingMatch =
                isPoco &&
                isPendingPocoMatch(match) &&
                isPendingPairLabel(pair1BaseLabel) &&
                isPendingPairLabel(pair2BaseLabel);
              if (isPendingMatch) {
                return null;
              }
              const pocoWinnerSide = isPoco ? resolvePocoWinnerSide(match) : null;
              const isPocoFinalRound =
                isPoco && roundIndex === pocoRoundCount - 1;
              const pair1Points = Number(
                pocoPointsByPairId[normalizePairId(match.dupla1Id)],
              );
              const pair2Points = Number(
                pocoPointsByPairId[normalizePairId(match.dupla2Id)],
              );
              const pair1Label =
                isPocoFinalRound && Number.isFinite(pair1Points)
                  ? `${pair1BaseLabel} (${pair1Points} pts)`
                  : pair1BaseLabel;
              const pair2Label =
                isPocoFinalRound && Number.isFinite(pair2Points)
                  ? `${pair2BaseLabel} (${pair2Points} pts)`
                  : pair2BaseLabel;
              return (
                <div key={`match-${roundIndex}-${matchIndex}`} className="jogo">
                  <span className="dupla">{pair1Label}</span>
                  {isPoco ? (
                    <div
                      className="poco-score-icons"
                      aria-label={
                        siteTexts.challenges.publicDetail.matches.matchResultAria
                      }
                    >
                      <span
                        className={`poco-score-icon ${
                          pocoWinnerSide === "dupla1"
                            ? "winner"
                            : pocoWinnerSide === "dupla2"
                              ? "loser"
                              : "pending"
                        }`}
                        aria-label={
                          pocoWinnerSide === "dupla1"
                            ? siteTexts.challenges.publicDetail.matches.champion
                            : pocoWinnerSide === "dupla2"
                              ? siteTexts.challenges.publicDetail.matches.loser
                              : siteTexts.challenges.publicDetail.matches
                                  .awaitingResult
                        }
                      >
                        {pocoWinnerSide === "dupla1"
                          ? "🏆"
                          : pocoWinnerSide === "dupla2"
                            ? "❌"
                            : "⏳"}
                      </span>
                      <span className="vs">x</span>
                      <span
                        className={`poco-score-icon ${
                          pocoWinnerSide === "dupla2"
                            ? "winner"
                            : pocoWinnerSide === "dupla1"
                              ? "loser"
                              : "pending"
                        }`}
                        aria-label={
                          pocoWinnerSide === "dupla2"
                            ? siteTexts.challenges.publicDetail.matches.champion
                            : pocoWinnerSide === "dupla1"
                              ? siteTexts.challenges.publicDetail.matches.loser
                              : siteTexts.challenges.publicDetail.matches
                                  .awaitingResult
                        }
                      >
                        {pocoWinnerSide === "dupla2"
                          ? "🏆"
                          : pocoWinnerSide === "dupla1"
                            ? "❌"
                            : "⏳"}
                      </span>
                    </div>
                  ) : (
                    <>
                      <input type="number" value={score1 === null ? "" : score1} disabled readOnly />
                      <span>X</span>
                      <input type="number" value={score2 === null ? "" : score2} disabled readOnly />
                    </>
                  )}
                  <span className="dupla">{pair2Label}</span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
